import React, { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { TrendingUpIcon } from 'lucide-react';
interface SparklineCardProps {
  value: number;
  trend: string;
  isInView: boolean;
  title?: string;
}
const SparklinePath = () => <svg width="240" height="120" viewBox="0 0 240 120" fill="none" className="absolute top-8" data-id="element-242">
    <defs data-id="element-243">
      <linearGradient id="sparkline-gradient" x1="0" y1="0" x2="1" y2="0" data-id="element-244">
        <stop offset="0%" stopColor="#14B8A6" data-id="element-245" />
        <stop offset="100%" stopColor="#2DD4BF" data-id="element-246" />
      </linearGradient>
      <linearGradient id="area-gradient" x1="0" y1="0" x2="0" y2="1" data-id="element-247">
        <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.15" data-id="element-248" />
        <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" data-id="element-249" />
      </linearGradient>
    </defs>
    {/* Area fill */}
    <motion.path d="M0 80C40 80 60 60 100 55C140 50 160 45 200 40C220 37.5 230 35 240 32L240 100L0 100Z" fill="url(#area-gradient)" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.3,
    delay: 0.3
  }} data-id="element-250" />
    {/* Main trend line */}
    <motion.path d="M0 80C40 80 60 60 100 55C140 50 160 45 200 40C220 37.5 230 35 240 32" stroke="url(#sparkline-gradient)" strokeWidth="2" strokeLinecap="round" fill="none" initial={{
    pathLength: 0
  }} animate={{
    pathLength: 1
  }} transition={{
    duration: 0.6,
    ease: 'easeOut'
  }} data-id="element-251" />
  </svg>;
export const SparklineCard = ({
  value,
  trend,
  isInView,
  title
}: SparklineCardProps) => {
  const [displayValue, setDisplayValue] = useState(0);
  const badgeControls = useAnimationControls();
  useEffect(() => {
    if (!isInView) {
      setDisplayValue(0);
      return;
    }
    let startTime: number;
    const duration = 420; // 0.42s for the count animation
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setDisplayValue(Math.round(progress * value));
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        badgeControls.start({
          scale: 1,
          opacity: 1
        });
      }
    };
    const animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, value]);
  return <div className="flex items-center justify-center relative" style={{
    width: '280px',
    height: '160px',
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    backdropFilter: 'blur(16px)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.10), 0 4px 8px rgba(0,0,0,0.06)',
    border: '4px solid transparent',
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.75), rgba(255, 255, 255, 0.75)), linear-gradient(to right, rgba(224, 215, 255, 0.4), rgba(183, 233, 233, 0.4))',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box'
  }} data-id="element-252">
      {/* Background overlay */}
      <div className="absolute inset-0 rounded-lg" style={{
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(15px)'
    }} data-id="element-253" />
      {title && <div className="absolute top-3 left-4 text-sm font-medium text-gray-600" data-id="element-254">
          {title}
        </div>}
      {/* Value */}
      <div className="text-[48px] font-bold text-[#6D5BD0] relative z-10 -mt-12" style={{
      fontFamily: 'Inter',
      fontFeatureSettings: '"tnum" 1'
    }} data-id="element-255">
        {displayValue}
      </div>
      {/* Sparkline */}
      {isInView && <SparklinePath data-id="element-256" />}
      {/* Trend Badge */}
      <motion.div className="absolute top-3 right-3 flex items-center bg-teal-500/10 px-2 py-0.5 rounded-full z-20" initial={{
      scale: 0.8,
      opacity: 0
    }} animate={badgeControls} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} data-id="element-257">
        <TrendingUpIcon size={12} className="text-teal-500 mr-0.5" data-id="element-258" />
        <span className="text-teal-500 text-xs font-medium" data-id="element-259">{trend}</span>
      </motion.div>
    </div>;
};